import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { QuestionInputField, QuestionInputFieldValue } from '/index';
import type { EmissionTypeOption } from '/business/shared/state/reducers/emissionTypes';

import {
  ERR_TYPE_ENUM_IS_DROPDOWN,
  ERR_TYPE_NUM_LARGE,
  ERR_TYPE_NUM_SMALL,
  ERR_TYPE_REQUIRED,
  ERR_TYPE_STR_LONG,
  ERR_TYPE_STR_SHORT,
  FIELD_TYPE_DROPDOWN,
  FIELD_TYPE_NUMBER,
  FIELD_TYPE_TXT,
  FIELD_TYPE_TXTAREA,
} from '/business/shared/components/questions/helper/fieldEnums';

type UseLeasedBuildingsSchemaProps = {
  dealershipName?: string;
  mappedForDropdown: EmissionTypeOption[];
};

type DropdownValue = {
  displayName?: string;
  displayNameKey: string;
  isPlaceholder?: boolean;
  name: string | null;
};

function getInputValue(value: QuestionInputFieldValue): string | null {
  const { inputValue } = value;
  return typeof inputValue === 'string' ? inputValue : null;
}

export default function useLeasedBuildingsSchema({ mappedForDropdown: fuelTypes }: UseLeasedBuildingsSchemaProps): {
  pageSchema: QuestionInputField[];
} {
  const { t } = useTranslation();

  const [valueFuelType, setValueFuelType] = useState<string | null>(null);

  const validationValue = 999999;

  const buildingIdentifier: QuestionInputField = useMemo(() => {
    return {
      propName: 'identifier',
      element: 'identifier',
      type: FIELD_TYPE_TXT,
      label: 'Building identifier',
      saveMeCallback: (value) => {
        const userVal = getInputValue(value);

        return {
          hasLeasedBuilds: true,
          hasNoData: false,
          identifier: userVal,
        };
      },
      validation: [
        {
          rule: ERR_TYPE_REQUIRED,
          errCode: 'ERRNODIESEL',
          errStr: 'Please enter a valid identifier',
        },
        {
          rule: ERR_TYPE_STR_LONG,
          errCode: 'ERRIDENTIFIERTOOLONG',
          errStr: 'Please enter a valid identifier',
          value: 300,
        },
      ],
    };
  }, []);

  const fuelType: QuestionInputField = useMemo(() => {
    const fuelTypeValues: DropdownValue[] = [
      {
        name: null,
        isPlaceholder: true,
        displayNameKey: 'business:QuestionPage.forms.otherFuels.selectFuel',
      },
      ...fuelTypes.map(({ displayName, name }) => {
        return {
          name,
          displayName,
          displayNameKey: 'business:emissionFactors.buildingFuelTypes.'.concat(displayName),
        };
      }),
    ];
    return {
      labelI18nKey: 'business:QuestionPage.forms.otherFuels.para3',
      type: FIELD_TYPE_DROPDOWN,
      values: fuelTypeValues,
      saveMeCallback: (value) => {
        const fuelTypeSlug = getInputValue(value);
        return {
          fuelType: fuelTypes.find(({ name }) => name === fuelTypeSlug),
          hasFuelType: true,
        };
      },
      onChangeCallback: (value) => {
        setValueFuelType(typeof value === 'string' ? value : null);
      },
      element: 'fuel_type',
      validation: [
        {
          rule: ERR_TYPE_REQUIRED,
          errCode: 'ERRNOFUELTYPE',
          errStr: t('business:Errors.fuelType.provideType'),
        },
        {
          rule: ERR_TYPE_ENUM_IS_DROPDOWN,
          errCode: 'ERRFUELTYPENOTFOUND',
          errStr: t('business:Errors.fuelType.notFound'),
        },
      ],
    };
  }, [fuelTypes, t]);

  const quantity: QuestionInputField = useMemo(() => {
    const fuelTypesUnits = fuelTypes.map((item) => {
      const typeUnitMapped = item?.units?.map((typeUnit) => {
        if (typeof typeUnit !== 'string') {
          return typeUnit;
        }
        const transLitDisplay = t('business:emissionFactors.volumes.'.concat(typeUnit), typeUnit);
        return {
          name: typeUnit,
          displayName: transLitDisplay,
        };
      });
      return {
        ...item,
        units: typeUnitMapped,
      };
    });
    return {
      element: 'quantity',
      propName: 'quantity',
      type: FIELD_TYPE_NUMBER,
      dropdown: {
        values: fuelTypesUnits?.find(({ name }) => name === valueFuelType)?.units,
        propName: 'fuelUnit',
        element: 'fuel_unit',
      },
      label: 'Quantity:',
      placeholderI18nKey: 'business:QuestionPage.forms.otherFuels.quantityPlaceholder',
      validation: [
        {
          rule: ERR_TYPE_REQUIRED,
          errCode: 'ERRNOQUANTITY',
          errStr: t('business:Errors.fuelType.fuelQuantity'),
        },
        {
          rule: ERR_TYPE_NUM_SMALL,
          errCode: 'ERRQUANTITYTOOLOW',
          errStr: t('business:Errors.fuelType.fuelQuantity'),
          value: 0,
        },
        {
          rule: ERR_TYPE_NUM_LARGE,
          errCode: 'ERRQUANTITYTOOHIGH',
          errStr: t('business:Errors.fuelType.fuelQuantity'),
          value: validationValue,
        },
      ],
    };
  }, [fuelTypes, valueFuelType, t]);

  const dataSource: QuestionInputField = useMemo(() => {
    return {
      type: FIELD_TYPE_TXTAREA,
      propName: 'dataSource',
      element: 'data_source',
      labelI18nKey: 'business:QuestionPage.forms.deicing.para5',
      description: <></>,
      validation: [
        {
          rule: ERR_TYPE_REQUIRED,
          errCode: 'ERRDATASOURCE',
          errStr: t('business:Errors.dataSource.provide'),
        },
        {
          rule: ERR_TYPE_STR_SHORT,
          errCode: 'ERRDATASOURCETOOSHORT',
          errStr: t('business:Errors.dataSource.tooShort'),
          value: 5,
        },
        {
          rule: ERR_TYPE_STR_LONG,
          errCode: 'ERRDATASOURCETOOLONG',
          errStr: t('business:Errors.dataSource.tooLong'),
          value: 3000,
        },
      ],
    };
  }, [fuelTypes, t]);

  const pageSchema: QuestionInputField[] = useMemo(
    () => [buildingIdentifier, fuelType, quantity, dataSource],
    [buildingIdentifier, fuelType, quantity, dataSource],
  );
  return {
    pageSchema,
  };
}
